/**
 * Shared footer rendered inside every MarketingLayout-wrapped page.
 * @file src/web/shared/components/Footer.tsx
 *
 * Admin pages use AdminShell (not MarketingLayout) and render without
 * a footer — the bottom-nav takes that real estate on mobile anyway.
 *
 * TODO: Swap the hard-coded company name + links for values pulled
 * from /api/settings/public (business_name, support_email). That way
 * the footer updates from the admin settings editor.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';

/**
 * Public-facing footer link set. Every payment-processor onboarding
 * form asks for a refund + cancellation URL; this is where those live.
 * Order: legal first, then contact — legal links get the most clicks
 * from processor review.
 */
const FOOTER_LINKS: Array<{ label: string; to: string }> = [
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'Terms', to: '/terms' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Refund', to: '/refund' },
  { label: 'Cancellation', to: '/cancellation' },
];

/**
 * Floating "Back to top" button. Appears in the bottom-right of the
 * viewport after the user has scrolled ~400px down, and scrolls back
 * to the top on click. Hidden until needed so short pages don't show
 * a pointless floating control.
 *
 * Lives inside the Footer component because visually it anchors to
 * the footer area — but the actual element is fixed-positioned so it
 * doesn't shift the footer's layout.
 */
function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      aria-label="Scroll to top"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-40 flex size-10 items-center justify-center rounded-full bg-inkblack text-highlighter shadow-lg transition-opacity hover:opacity-90"
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}

export const Footer: React.FC = () => {
  return (
    <>
      {/* Brand tokens rather than uikit's <Footer tone="subtle">, for the same
          reason as the header: that component paints uikit's palette, so these
          pages read as a different product from the landing page. */}
      <footer className="border-t border-receipt bg-receipt-40">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-6 py-8">
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {FOOTER_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-inkblack-60 no-underline transition-colors hover:text-inkblack"
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="border-t border-receipt pt-4 text-center">
            <p className="text-sm text-inkblack-60">
              © {new Date().getFullYear()} oddits — practice management for CAs.
            </p>
          </div>
        </div>
      </footer>
      <BackToTopButton />
    </>
  );
};
